import { useI18n } from '../i18n';
import type { PartnerHour } from '../lib/admin';

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

/// Shared weekly-hours editor used by the admin partner modal and the
/// partner-self page. Renders one row per weekday, Mon-first, with zero
/// or more time slots per day. Each slot is two HH:MM inputs and a
/// remove button; "+ Add slot" appends a new slot to that day.
///
/// Slots that wrap into the following morning (e.g. Fri 22:00 → 02:00)
/// are entered as a single slot with closesAt <= opensAt — the backend
/// handles the wrap, the editor only validates that the two times
/// differ.
export function PartnerHoursEditor({
  hours,
  onChange,
  disabled,
}: {
  hours: PartnerHour[];
  onChange: (next: PartnerHour[]) => void;
  disabled?: boolean;
}) {
  const { t } = useI18n();

  function slotsFor(day: number): Array<{ idx: number; slot: PartnerHour }> {
    const out: Array<{ idx: number; slot: PartnerHour }> = [];
    hours.forEach((s, i) => {
      if (s.weekday === day) out.push({ idx: i, slot: s });
    });
    out.sort((a, b) => a.slot.opensAt.localeCompare(b.slot.opensAt));
    return out;
  }

  function addSlot(day: number) {
    onChange([
      ...hours,
      { weekday: day, opensAt: '17:00', closesAt: '23:00' },
    ]);
  }

  function removeSlot(idx: number) {
    onChange(hours.filter((_, i) => i !== idx));
  }

  function updateSlot(idx: number, patch: Partial<PartnerHour>) {
    onChange(hours.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  }

  return (
    <div class="partner-hours-grid">
      {DAY_KEYS.map((key, day) => {
        const slots = slotsFor(day);
        return (
          <div class="partner-hours-row" key={key}>
            <div class="partner-hours-day">{t(`partner.hours.day_${key}`)}</div>
            <div class="partner-hours-slots">
              {slots.length === 0 && (
                <span class="partner-hours-closed">
                  {t('partner.hours.closed_day')}
                </span>
              )}
              {slots.map(({ idx, slot }) => (
                <div class="partner-hours-slot" key={idx}>
                  <input
                    type="time"
                    value={slot.opensAt}
                    disabled={disabled}
                    aria-label={t('partner.hours.from')}
                    onInput={(e) =>
                      updateSlot(idx, {
                        opensAt: (e.target as HTMLInputElement).value,
                      })
                    }
                  />
                  <span class="partner-hours-dash" aria-hidden="true">–</span>
                  <input
                    type="time"
                    value={slot.closesAt}
                    disabled={disabled}
                    aria-label={t('partner.hours.to')}
                    onInput={(e) =>
                      updateSlot(idx, {
                        closesAt: (e.target as HTMLInputElement).value,
                      })
                    }
                  />
                  {!disabled && (
                    <button
                      type="button"
                      class="partner-hours-remove"
                      aria-label={t('partner.hours.remove_slot')}
                      onClick={() => removeSlot(idx)}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              {!disabled && (
                <button
                  type="button"
                  class="partner-hours-add"
                  onClick={() => addSlot(day)}
                >
                  {t('partner.hours.add_slot')}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/// Local validator shared by both consumers. Returns the canonicalized
/// hours array (trimmed times) on success, or an error message string
/// to surface in the form. The backend re-validates, but failing fast
/// here prevents the round-trip on obvious typos.
export function validateHours(
  hours: PartnerHour[],
  t: (k: string) => string,
): { ok: true; hours: PartnerHour[] } | { ok: false; error: string } {
  const cleaned: PartnerHour[] = [];
  for (const h of hours) {
    const o = h.opensAt?.trim() ?? '';
    const c = h.closesAt?.trim() ?? '';
    if (!/^\d{2}:\d{2}$/.test(o) || !/^\d{2}:\d{2}$/.test(c)) {
      return { ok: false, error: t('partner.hours.invalid_time') };
    }
    if (o === c) {
      return { ok: false, error: t('partner.hours.same_time') };
    }
    cleaned.push({ weekday: h.weekday, opensAt: o, closesAt: c });
  }
  return { ok: true, hours: cleaned };
}
